import type { PaigeClient } from '@paige/api-client';
import type { AdminApi } from './data-source';
import { periodApi } from './period-api';
import type {
  Customer,
  CustomerAction,
  AdminPools,
  AdminHealth,
  CampaignSpend,
  TenantAnalytics,
  SupportAccessGrant,
  SupportAccessRequest,
} from './types';
import { unwrap } from './http';
import { insightsApi } from './insights-api';
import { customersApi } from './customers-api';
import { flowApi } from './flow-api';
import { flowIncidentsApi } from './flow-incidents-api';
import { problemsApi, workApi } from './problems-api';
import { timelineApi } from './timeline-api';
import { gpuApi } from './gpu-api';
import { supportFixesApi } from './support-fixes-api';

/** The staff scope (`/admin/...`), the same endpoints Phone Home calls (§15.7). */
export function adminApi(client: PaigeClient): AdminApi {
  return {
    async getCustomer(tenantId) {
      const r = await client.GET('/admin/tenants/{tenantId}', { params: { path: { tenantId } } });
      return unwrap(r) as Customer;
    },
    ...insightsApi(client),
    ...customersApi(client),
    customerAction: (tenantId, action) => customerAction(client, tenantId, action),
    grantAccess: (tenantId, request) => grantAccess(client, tenantId, request),
    async endAccess(tenantId) {
      unwrap(
        await client.DELETE('/admin/tenants/{tenantId}/access', { params: { path: { tenantId } } }),
      );
    },
    ...flowApi(client),
    ...flowIncidentsApi(client),
    ...problemsApi(client),
    ...workApi(client),
    ...supportFixesApi(client),
    ...timelineApi(client),
    async getPools() {
      return unwrap(await client.GET('/admin/pools')) as AdminPools;
    },
    async getHealth() {
      return unwrap(await client.GET('/admin/health')) as AdminHealth;
    },
    ...gpuApi(client),
    ...periodApi(client),
    enterCampaignSpend: (entry) => enterCampaignSpend(client, entry),
    getTenantAnalytics: (tenantId) => getTenantAnalytics(client, tenantId),
  };
}

/** Open a company as support (§14.4): the API answers the recorded, time-boxed grant. */
async function grantAccess(
  client: PaigeClient,
  tenantId: string,
  request: SupportAccessRequest,
): Promise<SupportAccessGrant> {
  const r = await client.POST('/admin/tenants/{tenantId}/access', {
    params: { path: { tenantId } },
    // Read-only unless asked: the API refuses a write grant to a role that may not act.
    body: { ...request, mode: request.mode ?? 'read' },
  });
  return unwrap(r) as SupportAccessGrant;
}

/** A recorded support action (§15.7); the idempotency key is filled by the client's request hook. */
async function customerAction(
  client: PaigeClient,
  tenantId: string,
  action: CustomerAction,
): Promise<Customer> {
  const r = await client.POST('/admin/tenants/{tenantId}/actions', {
    params: { header: { 'Idempotency-Key': '' }, path: { tenantId } },
    body: action,
  });
  return unwrap(r) as Customer;
}

async function getTenantAnalytics(client: PaigeClient, tenantId: string): Promise<TenantAnalytics> {
  const r = await client.GET('/admin/tenants/{tenantId}/analytics', {
    params: { path: { tenantId } },
  });
  return unwrap(r) as TenantAnalytics;
}

async function enterCampaignSpend(client: PaigeClient, entry: CampaignSpend): Promise<void> {
  unwrap(await client.PUT('/admin/campaigns/spend', { body: entry }));
}
